import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, CheckCircle, Eye, EyeOff, GitBranch, Mail } from 'lucide-react';
import { useDataProvider, useGetList, useNotify, useRecordContext } from 'ra-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { AddressBlock } from '../shared/AddressBlock';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { QuotationEmailModal } from './QuotationEmailModal';
import { QuotationPDFButton } from './QuotationPDFButton';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Arial:wght@400;700&display=swap');

  .po-wrap {
    font-family: Arial, sans-serif;
    font-size: 12px;
    color: #000;
    background: #e8eaf0;
    min-height: 100vh;
    padding: 28px 20px 48px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .po-sheet {
    background: #fff;
    max-width: 960px;
    margin: 0 auto;
    padding: 20px 24px 24px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.12);
    border: 1px solid #ccc;
    position: relative;
  }

  /* ── HEADER ── */
  .po-header-bar {
    display: grid;
    grid-template-columns: 200px 1fr 240px;
    align-items: center;
    margin-bottom: 0;
    border: 1.5px solid #000;
  }
  .po-header-logo {
    padding: 10px 12px;
    border-right: 1.5px solid #000;
  }
  .po-header-logo img { width: 160px; height: auto; display: block; }
  .po-header-title {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 10px;
  }
  .po-header-meta {
    border-left: 1.5px solid #000;
    padding: 8px 12px;
    font-size: 12px;
  }
  .po-meta-row {
    display: grid;
    grid-template-columns: 80px 10px 1fr;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 11px;
    align-items: center;
  }
  .po-meta-lbl { font-weight: 700; white-space: nowrap; text-transform: uppercase; color: #555; }
  .po-meta-val { font-weight: 700; color: #000; white-space: nowrap; }

    border-top: none;
    margin-bottom: 0;
  }
  .ship-to-inner {
    display: grid;
    grid-template-columns: 200px 1fr;
  }
  .ship-to-lbl {
    font-weight: 700;
    font-size: 11px;
    padding: 6px 10px;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: flex-start;
    background: #f0f0f0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .ship-to-body { padding: 6px 12px; font-size: 11px; line-height: 1.6; }

  /* ── PAYMENT / REMARKS AREA ── */
  .po-remarks-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1.5px solid #000;
    border-top: none;
    margin-bottom: 0;
  }
  .remarks-col {
    padding: 6px 12px;
    font-size: 11px;
    border-right: 1.5px solid #000;
  }
  .remarks-col:last-child { border-right: none; }

  /* ── DEAR SIR ── */
  .dear-sir {
    padding: 8px 0 4px;
    font-size: 12px;
  }

  /* ── ITEMS TABLE ── */
  .items-tbl {
    width: 100%;
    border-collapse: collapse;
    border: 1.5px solid #000;
    font-size: 11px;
    margin-bottom: 0;
  }
  .items-tbl thead tr { background: #e8e8e8; }
  .items-tbl th {
    padding: 7px 6px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    border: 1px solid #000;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .items-tbl th.left { text-align: left; }
  .items-tbl td {
    padding: 8px 6px;
    border: 1px solid #000;
    text-align: center;
    vertical-align: middle;
  }
  .items-tbl td.left { text-align: left; }
  .items-tbl td.right { text-align: right; }
  .item-code { font-size: 10px; color: #666; }
  .item-desc-main { font-weight: 700; font-size: 12px; }
  .item-desc-sub { font-size: 10px; color: #555; }

  /* ── TOTALS ── */
  .totals-section {
    border: 1.5px solid #000;
    border-top: none;
    display: grid;
    grid-template-columns: 1fr 280px;
  }
  .amount-words {
    padding: 8px 12px;
    font-size: 11px;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }
  .amount-words b { font-size: 12px; margin-right: 4px; }
  .totals-right { display: flex; flex-direction: column; }
  .t-row {
    display: flex;
    justify-content: space-between;
    padding: 5px 12px;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
  }
  .t-row:last-child { border-bottom: none; }
  .t-row-lbl { font-weight: 700; color: #333; font-size: 11px; }
  .grand-total-bar {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background: #1a1a1a;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
  }

  /* ── INSTRUCTIONS / T&C ── */
  .instructions-section {
    border: 1.5px solid #000;
    border-top: none;
  }
  .instr-row {
    display: flex;
    border-bottom: 1px solid #ccc;
  }
  .instr-row:last-child { border-bottom: none; }
  .instr-lbl {
    font-weight: 700;
    font-size: 11px;
    min-width: 140px;
    padding: 6px 12px;
    border-right: 1px solid #ccc;
    background: #f9f9f9;
    display: flex;
    align-items: flex-start;
  }
  .instr-val {
    padding: 6px 12px;
    font-size: 11px;
    line-height: 1.6;
    flex: 1;
  }
  .tc-section {
    border: 1.5px solid #000;
    border-top: none;
    padding: 10px 14px;
    font-size: 11px;
    line-height: 1.7;
  }
  .tc-title {
    font-weight: 700;
    font-size: 12px;
    margin-bottom: 6px;
    text-decoration: underline;
  }

  /* ── SIGNATORY ── */
  .signatory-section {
    border: 1.5px solid #000;
    border-top: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .signatory-col {
    padding: 8px 14px 12px;
    border-right: 1.5px solid #000;
    text-align: center;
    font-size: 11px;
    position: relative;
  }
  .signatory-col:last-child { border-right: none; }
  .signatory-space { height: 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .signatory-lbl { font-weight: 700; font-size: 12px; }
  .stamp-img { height: 70px; width: auto; object-fit: contain; opacity: 0.92; }
  .sign-img { height: 36px; width: auto; object-fit: contain; opacity: 0.9; position: absolute; bottom: 4px; right: 10px; }

  /* ── FOOTER ── */
  .po-footer {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #666;
  }
  .po-footer-center {
    text-align: center;
    margin-top: 0;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid #000;
    border-top: none;
    padding: 7px;
    letter-spacing: 0.06em;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  /* ── PRINT STYLES ── */
  @media print {
    html, body {
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden;
    }
    .po-wrap, .po-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-wrap {
      position: absolute;
      left: 0;
      top: 0;
      margin: 0;
      padding: 0;
      background: white;
      width: 100%;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-sheet {
      display: flex !important;
      flex-direction: column !important;
      min-height: calc(297mm - 16mm) !important;
      box-shadow: none;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      max-width: 100%;
    }
    .print-border {
      display: block !important;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1.5px solid #000;
      pointer-events: none;
      z-index: 10000;
    }
    .print-spacer {
      display: block !important;
      flex-grow: 1 !important;
    }
    .status-row {
      display: none !important;
    }
    .items-tbl tr {
      page-break-inside: avoid;
    }
    .totals-section, .signatory-section, .po-footer-center, .po-footer {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @page {
      margin: 8mm;
      size: A4;
    }
    .print-hide {
      display: none !important;
    }
    .address-block {
      margin-bottom: 0 !important;
      padding: 5px 10px !important;
    }
    .address-block * {
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .items-tbl th, .items-tbl td {
      padding: 5px 6px !important;
      font-size: 10px !important;
      line-height: 1.35 !important;
    }
    .totals-section {
      grid-template-columns: 1fr 240px !important;
    }
    .totals-section .amount-words {
      padding: 6px 10px !important;
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .totals-section .t-row {
      padding: 4px 10px !important;
      font-size: 11px !important;
    }
    .grand-total-bar {
      padding: 6px 10px !important;
      font-size: 12px !important;
    }
    .signatory-col {
      padding: 6px 10px !important;
    }
    .signatory-space {
      height: 50px !important;
    }
    .stamp-img {
      height: 45px !important;
    }
    .tc-section {
      padding: 4px 10px !important;
      font-size: 7.5px !important;
      line-height: 1.25 !important;
    }
    .tc-title {
      margin-bottom: 2px !important;
      font-size: 9px !important;
    }
    .po-header-logo {
      padding: 6px 10px !important;
    }
    .po-header-logo img {
      width: 125px !important;
    }
    .po-header-title {
      font-size: 15px !important;
      padding: 6px !important;
    }
    .po-header-meta {
      padding: 6px 10px !important;
    }
    .po-meta-row {
      margin-bottom: 2px !important;
      font-size: 10px !important;
    }
    .dear-sir {
      padding: 4px 0 !important;
      font-size: 11px !important;
    }
  }
`;

const inr = (val: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

// QTN-6: contact person's phone was missing on the quotation print. Rendered as
// a child of <ReferenceField source="customer_id" reference="contacts">, so the
// referenced contact is in context. Phones live in phone_jsonb ([{type,number}]).
const ContactPhone = () => {
    const contact = useRecordContext();
    const phones = Array.isArray(contact?.phone_jsonb) ? contact.phone_jsonb : [];
    const numbers = phones
        .map((p: any) => p?.number)
        .filter(Boolean)
        .join(', ');
    return <>{numbers || '-'}</>;
};

const amountInWords = (amount: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const numToWords = (n: number): string => {
        if (n === 0) return '';
        if (n < 20) return ones[n] + ' ';
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
        if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + numToWords(n % 100);
        if (n < 100000) return numToWords(Math.floor(n / 1000)) + 'Thousand ' + numToWords(n % 1000);
        if (n < 10000000) return numToWords(Math.floor(n / 100000)) + 'Lakh ' + numToWords(n % 100000);
        return numToWords(Math.floor(n / 10000000)) + 'Crore ' + numToWords(n % 10000000);
    };

    const wholePart = Math.floor(amount);
    const decimalPart = Math.round((amount - wholePart) * 100);
    let result = 'INR ' + numToWords(wholePart).trim() + ' Rupees';
    if (decimalPart > 0) result += ' and ' + numToWords(decimalPart).trim() + ' Paise';
    result += ' Only';
    return result;
};

const QuotationShowActions = ({ onEmailClick, showWatermark, onToggleWatermark }: { onEmailClick: () => void; showWatermark: boolean; onToggleWatermark: () => void }) => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { hasPermission: canManage } = useHasPermission('manage_documents');

    // Gate: a quotation should only spawn one PI and one SO. If a downstream
    // document already references this quotation, disable the convert action so
    // users don't create duplicates (which also triggered "already generated"
    // / duplicate-key bugs). See bug tracker QTN #14/#17.
    const { data: existingPIs } = useGetList(
        'proforma_invoices',
        { filter: { quotation_id: record?.id }, pagination: { page: 1, perPage: 1 } },
        { enabled: !!record?.id },
    );
    const { data: existingSOs } = useGetList(
        'sales_orders',
        { filter: { quotation_id: record?.id }, pagination: { page: 1, perPage: 1 } },
        { enabled: !!record?.id },
    );
    const hasPI = (existingPIs?.length ?? 0) > 0;
    const hasSO = (existingSOs?.length ?? 0) > 0;

    const handleConvertToPI = () => {
        if (!record || hasPI) return;
        const { id: _id, reference: _reference, date: _date, valid_until: _valid_until, status: _status, created_at: _created_at, updated_at: _updated_at, terms_conditions: _terms_conditions, approved_at: _approved_at, approved_by: _approved_by, sent_at: _sent_at, base_quotation_id: _bqid, revision_number: _rn, notes: _notes, description: _desc, markup_percentage: _markup, ...restRecord } = record;
        navigate('/proforma_invoices/create', {
            state: {
                record: {
                    ...restRecord,
                    quotation_id: record.id,
                    company_id: record.company_id,
                    project_id: record.project_id,
                    customer_id: record.customer_id,
                    status: 'draft',
                    items: record.items?.map((item: any) => {
                        const { id: _id, quotation_id: _quotation_id, cost_price: _cost_price, ...itemRest } = item;
                        return itemRest;
                    }) || []
                }
            }
        });
    };

    const handleApproveAndCreateSO = async () => {
        if (!record || hasSO) return;

        try {
            await dataProvider.update('quotations', {
                id: record.id,
                data: { status: 'approved' },
                previousData: record
            });
            notify('Quotation approved', { type: 'success' });

            const { id: _id, reference: _reference, date: _date, status: _status, valid_until: _valid_until, created_at: _created_at, updated_at: _updated_at, terms_conditions: _terms_conditions, markup_percentage: _markup, ...restRecord } = record;
            navigate('/sales_orders/create', {
                state: {
                    record: {
                        ...restRecord,
                        quotation_id: record.id,
                        status: 'draft',
                        items: record.items?.map((item: any) => {
                            const { id: _id, quotation_id: _quotation_id, cost_price: _cost_price, ...itemRest } = item;
                            return itemRest;
                        }) || []
                    }
                }
            });
        } catch {
            notify('Error approving quotation', { type: 'error' });
        }
    };

    const handleCreateRevision = async () => {
        if (!record) return;

        try {
            // Mark original as 'revised'
            await dataProvider.update('quotations', {
                id: record.id,
                data: { status: 'revised' },
                previousData: record
            });

            // Determine base quotation id and next revision number
            const baseQuotationId = record.base_quotation_id ?? record.id;
            const nextRevision = (record.revision_number ?? 0) + 1;

            // Build new reference: e.g. "QT-001" → "QT-001/R1", or "QT-001/R1" → "QT-001/R2"
            const baseRef = record.base_quotation_id
                ? (record.reference as string).replace(/\/R\d+$/, '')
                : record.reference as string;
            const newReference = `${baseRef}/R${nextRevision}`;

            // Clone line items (strip DB ids)
            const clonedItems = (record.items ?? []).map((item: any) => {
                const { id: _id, quotation_id: _quotation_id, ...rest } = item;
                return rest;
            });

            // Create the new revision - ONLY include fields that exist in DB schema
            const revisionData = {
                reference: newReference,
                customer_name: record.customer_name,
                customer_id: record.customer_id,
                company_id: record.company_id,
                project_id: record.project_id,
                date: new Date().toISOString().split('T')[0],
                valid_until: record.valid_until,
                status: 'draft',
                base_quotation_id: baseQuotationId,
                revision_number: nextRevision,
                items: clonedItems,
                subtotal: record.subtotal,
                tax_amount: record.tax_amount,
                total_amount: record.total_amount,
                currency: record.currency ?? 'INR',
                terms_conditions: record.terms_conditions,
                notes: record.notes,
            };

            const { data: newQuotation } = await dataProvider.create('quotations', {
                data: revisionData
            });

            notify(`Revision ${newReference} created`, { type: 'success' });
            navigate(`/quotations/${newQuotation.id}/show`);
        } catch (error) {
            console.error('Revision creation error:', error);
            notify('Error creating revision', { type: 'error' });
        }
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Button
                variant={showWatermark ? "secondary" : "outline"}
                size="sm"
                onClick={onToggleWatermark}
                className={showWatermark ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
            >
                {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Watermark: {showWatermark ? "ON" : "OFF"}
            </Button>
            <EditButton />
            <Button variant="outline" size="sm" onClick={onEmailClick}>
                <Mail className="w-4 h-4 mr-2" />
                Send Email
            </Button>
            <QuotationPDFButton />

            {/* Create Revision — gated by manage_documents (revision = create + status update) */}
            {canManage && record?.status !== 'revised' && (
                <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    onClick={handleCreateRevision}
                >
                    <GitBranch className="w-4 h-4 mr-2" />
                    Create Revision
                </Button>
            )}
            <span title={hasPI ? 'A Proforma Invoice has already been generated from this quotation' : undefined}>
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleConvertToPI}
                    disabled={hasPI}
                >
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    {hasPI ? 'PI Already Generated' : 'Convert to PI'}
                </Button>
            </span>
            {canManage && (record?.status === 'sent' || hasSO) && (
                <span title={hasSO ? 'A Sales Order has already been generated from this quotation' : undefined}>
                    <Button
                        variant="outline"
                        size="sm"
                        className="bg-green-600 text-white hover:bg-green-700 hover:text-white"
                        onClick={handleApproveAndCreateSO}
                        disabled={hasSO}
                    >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {hasSO ? 'SO Already Generated' : 'Approve & Create SO'}
                    </Button>
                </span>
            )}
        </div>
    );
};

const QuotationShowContent = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext();
    const config = useConfigurationContext();
    if (!record) return null;

    return (
        <>
            <style>{css}</style>
            <div className="po-wrap">
                <div className="po-sheet">
                    <div className="print-border" />
                    <WatermarkOverlay isVisible={showWatermark} />

                    {/* ── HEADER BAR ── */}
                    <div className="po-header-bar">
                        <div className="po-header-logo">
                            <img src="/logos/rovasys_horizontal_clean.png" alt="Rovasys" />
                            <div style={{ fontSize: 9, color: '#555', marginTop: 4, lineHeight: 1.4 }}>
                                W-145, S-Block, MIDC-Bhosari, Pune - 411026<br />
                                Ph: +91 91722 01844 | info@rovasys.com
                            </div>
                        </div>
                        <div className="po-header-title">Quotation</div>
                        <div className="po-header-meta">
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">REF NO</span>
                                <span>:</span>
                                <span className="po-meta-val" style={{ whiteSpace: 'nowrap' }}>{record.reference}</span>
                            </div>
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Date</span>
                                <span>:</span>
                                <span className="po-meta-val"><DateField source="date" /></span>
                            </div>
                            {record.valid_until && (
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl" style={{ color: '#b45309' }}>Valid Until</span>
                                    <span style={{ color: '#b45309' }}>:</span>
                                    <span className="po-meta-val" style={{ color: '#b45309' }}><DateField source="valid_until" /></span>
                                </div>
                            )}
                            <div className="po-meta-row">
                                <span className="po-meta-lbl">Status</span>
                                <span>:</span>
                                <span className="po-meta-val" style={{ textTransform: 'uppercase' }}>{record.status}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── FROM / TO ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1.5px solid #000', borderTop: 'none', marginBottom: 0 }}>
                        {/* FROM */}
                        <div style={{ borderRight: '1.5px solid #000' }}>
                            <AddressBlock label="Supplier Details" addressOverride={
                                <>
                                    <strong>ROVASYS ALLIED WORKS PVT. LTD.</strong><br/>
                                    W-145, S-Block, MIDC-Bhosari,<br />
                                    Pune, Maharashtra – 411026<br />
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>Ph</span><span>: +91 91722 01844</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>E-mail</span><span>: info@rovasys.com</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>GSTIN</span><span>: 27AAMCR6049R1ZD</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>PAN</span><span>: AAMCR6049R</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>State Code</span><span>: 27</span></div>
                                </>
                            } />
                        </div>

                        {/* TO (QTN-6: prefer quotation's own Bill To address, fall back to company master) */}
                        <div>
                            <AddressBlock
                                label="Bill To"
                                companyId={record.company_id}
                                addressOverride={
                                    record.billing_address !== null && record.billing_address !== undefined
                                    ? (record.billing_address ? (
                                        <>
                                            <strong>
                                                <ReferenceField source="company_id" reference="companies" link={false}>
                                                    <TextField source="name" />
                                                </ReferenceField>
                                            </strong>
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{record.billing_address}</div>
                                        </>
                                    ) : (
                                        <div style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', color: '#666' }}>Not provided.</div>
                                    ))
                                    : undefined
                                }
                            >
                                <div style={{ display: 'flex', gap: 4, marginTop: 6, paddingTop: 6, borderTop: '1px solid #ddd' }}>
                                    <span style={{ fontWeight: 700, minWidth: 90 }}>Contact Person:</span>
                                    <span>
                                        <ReferenceField source="customer_id" reference="contacts" link={false}>
                                            <TextField source="first_name" />{' '}<TextField source="last_name" />
                                        </ReferenceField>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <span style={{ fontWeight: 700, minWidth: 90 }}>Contact No:</span>
                                    <span>
                                        <ReferenceField source="customer_id" reference="contacts" link={false}>
                                            <ContactPhone />
                                        </ReferenceField>
                                    </span>
                                </div>
                            </AddressBlock>
                        </div>
                    </div>

                    {/* ── SHIP TO (QTN-6: prefer quotation's own Ship To, fall back to company master) ── */}
                    <div className="ship-to-bar border-l border-r border-b border-black">
                        <AddressBlock
                            companyId={record.company_id}
                            useShipping
                            label="Ship To"
                            addressOverride={
                                record.shipping_address !== null && record.shipping_address !== undefined
                                ? (record.shipping_address ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{record.shipping_address}</div>
                                ) : (
                                    <div style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', color: '#666' }}>Not provided.</div>
                                ))
                                : undefined
                            }
                        />
                    </div>

                    {/* ── DEAR SIR ── */}
                    <div className="dear-sir">
                        Dear Sir,<br />
                        <span style={{ marginLeft: 20 }}>Please find our best quotation for the items listed below as per your requirement.</span>
                    </div>

                    {/* ── ITEMS TABLE ── */}
                    <table className="items-tbl">
                        <thead>
                            <tr>
                                <th style={{ width: 44, whiteSpace: 'nowrap' }}>S.No.</th>
                                <th className="left">Description</th>
                                <th style={{ width: 80 }}>HSN / SAC</th>
                                <th style={{ width: 56 }}>GST %</th>
                                <th style={{ width: 68 }}>QTY</th>
                                <th style={{ width: 52 }}>UOM</th>
                                <th style={{ width: 100 }}>Rate</th>
                                <th style={{ width: 110 }}>Total Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {record.items?.map((item: any, index: number) => (
                                <tr key={index}>
                                    <td>{String(index + 1).padStart(5, '0')}</td>
                                    <td className="left">
                                        <div className="item-desc-main">{item.description}</div>
                                        {(item.sub_items ?? []).map((sub: any, si: number) => (
                                            <div key={si} style={{ fontSize: 10, color: '#444', marginTop: 2, paddingLeft: 8 }}>
                                                {si + 1}) {sub.text ?? sub}
                                                {sub.qty_note && <strong style={{ marginLeft: 6 }}>TOTAL {sub.qty_note}</strong>}
                                            </div>
                                        ))}
                                    </td>
                                    <td>{item.hsn_code || '-'}</td>
                                    <td>{item.tax_rate ?? 18}%</td>
                                    <td style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN').format(item.quantity)}</td>
                                    <td>{item.unit || 'Nos'}</td>
                                    <td className="right">{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.unit_price)}</td>
                                    <td className="right" style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.line_total)}</td>
                                </tr>
                            ))}
                            {/* padding row */}
                            <tr className="print-hide">
                                <td colSpan={8} style={{ height: 40, borderBottom: 'none' }} />
                            </tr>
                        </tbody>
                    </table>

                    {/* ── TOTALS ── */}
                    <div className="totals-section">
                        <div className="amount-words">
                            <div>
                                <b>Amount In Words:</b>
                                <span style={{ color: '#222' }}> {amountInWords(record.total_amount)}</span>
                            </div>
                        </div>
                        <div className="totals-right">
                            <div className="t-row">
                                <span className="t-row-lbl">Sub Total</span>
                                <span style={{ fontWeight: 700 }}>{inr(record.subtotal)}</span>
                            </div>
                            <div className="t-row">
                                <span className="t-row-lbl">GST @ 18.00%</span>
                                <span style={{ fontWeight: 700 }}>{inr(record.tax_amount)}</span>
                            </div>
                            <div className="grand-total-bar">
                                <span style={{ letterSpacing: '0.08em' }}>GRAND TOTAL</span>
                                <span style={{ fontSize: 14 }}>{inr(record.total_amount)}</span>
                            </div>
                        </div>
                    </div>



                    {/* ── TERMS & CONDITIONS ── */}
                    <div className="print-spacer" />

                    <div className="footer-group">
                        {/* ── TERMS & CONDITIONS ── */}
                        {record.terms_conditions && (
                            <div className="tc-section">
                                <div className="tc-title">Terms &amp; Conditions :</div>
                                <TextField source="terms_conditions" />
                            </div>
                        )}

                        {/* ── SIGNATORY ── */}
                        <div className="signatory-section">
                            <div className="signatory-col">
                                <div className="signatory-space" />
                                <div className="signatory-lbl">Prepared By</div>
                            </div>
                            <div className="signatory-col">
                                <div className="signatory-space">
                                    {config.enableAutomatedSignatures && (
                                        <>
                                            <img
                                                src="/img/rovasys-stamp.png"
                                                alt="Company Stamp"
                                                className="stamp-img"
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="signatory-lbl">Authorised Signatory</div>
                                {config.enableAutomatedSignatures && (
                                    <div style={{ fontSize: 11, color: '#555' }}>Rovasys Allied Works Pvt Ltd</div>
                                )}
                            </div>
                        </div>

                        {/* ── FOOTER ── */}
                        <div className="po-footer-center">
                            System generated Document, Does not require any signature.
                        </div>

                        <div className="po-footer">
                            <span>Page: 1 / 1</span>
                            {config.showPrintTimestamp && (
                                <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const QuotationShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/quotations" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Quotations
                </Link>
            </Button>
            <span className="text-2xl font-bold tracking-tight">Quotation {record.reference}</span>
        </div>
    );
};

export const QuotationShow = () => {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [showWatermark, setShowWatermark] = useState(true);

    return (
        <div className="flex flex-col gap-4 p-6">
            <Show
                title={<QuotationShowTitle />}
                actions={
                    <QuotationShowActions
                        onEmailClick={() => setEmailModalOpen(true)}
                        showWatermark={showWatermark}
                        onToggleWatermark={() => setShowWatermark(!showWatermark)}
                    />
                }
                className="mt-0"
            >
                <QuotationShowContent showWatermark={showWatermark} />
                <QuotationEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
            </Show>
        </div>
    );
};